import { z } from "zod"
import { MissionProvider } from "../workspace/flight-records"
import { latitudeSchema, longitudeSchema } from "../util"

const CORNER_SEPARATOR = "_"
const AXIS_SEPARATOR = ":"

export enum IngestDataType {
    Copy = "copy",
    InfraredImage = "infrared-image",
    OrthoImage = "ortho-image",
    Panorama = "panorama-image",
    ColorFinderImage = "color-finder-image",
    IconImage = "icon-image",
    ProfilePhoto = "profile-photo",
    FlightRecord = "flight-record",
    CsvLogbook = "csv-logbook",
    CommentImage = "comment-image",
    PdfRaster = "pdf-raster",
    // @deprecated
    // remove anytime after deploy of PR3750
    RapidModeImage = "rapid-mode-image",
}

export const baseTagsSchema = z.object({
    orgId: z.string(),
    dataType: z.enum(IngestDataType),
    projectId: z.string().optional(),
})

export type BaseTags = z.infer<typeof baseTagsSchema>

export const copyIngestPayload = baseTagsSchema.extend({
    dataType: z.literal(IngestDataType.Copy),
    nextPath: z.string(),
})

export type CopyIngestPayload = z.infer<typeof copyIngestPayload>

export const infraredImageIngestPayload = baseTagsSchema.extend({
    dataType: z.literal(IngestDataType.InfraredImage),
    projectId: z.string(),
    flightId: z.string(),
    compression: z.enum(["gzip"]).optional(),
})

export type InfraredImageIngestPayload = z.infer<typeof infraredImageIngestPayload>

export const orthoImageIngestPayload = baseTagsSchema.extend({
    dataType: z.literal(IngestDataType.OrthoImage),
    projectId: z.string(),
    flightId: z.string(),
    totalCount: z.coerce.number().optional(),
})
export type OrthoImageIngestPayload = z.infer<typeof orthoImageIngestPayload>

export const panoramaIngestPayload = baseTagsSchema.extend({
    dataType: z.literal(IngestDataType.Panorama),
    projectId: z.string(),
    flightId: z.string(),
})
export type PanoramaIngestPayload = z.infer<typeof panoramaIngestPayload>

export const colorFinderImageIngestPayload = baseTagsSchema.extend({
    dataType: z.literal(IngestDataType.ColorFinderImage),
    projectId: z.string(),
    flightId: z.string(),
})
export type ColorFinderImageIngestPayload = z.infer<typeof colorFinderImageIngestPayload>

export const iconImageIngestPayload = baseTagsSchema.extend({
    dataType: z.literal(IngestDataType.IconImage),
    projectId: z.string(),
    iconId: z.string(),
    index: z.string(),
    fileName: z.string().optional(),
})
export type IconImageIngestPayload = z.infer<typeof iconImageIngestPayload>

export const profilePhotoIngestPayload = baseTagsSchema.extend({
    dataType: z.literal(IngestDataType.ProfilePhoto),
    nextPath: z.string(),
})

export type ProfilePhotoIngestPayload = z.infer<typeof profilePhotoIngestPayload>
export const flightRecordIngestPayload = baseTagsSchema.extend({
    dataType: z.literal(IngestDataType.FlightRecord),
    fileId: z.string(),
    provider: z.enum(MissionProvider),
    pilotUserId: z.string().optional(),
})

export type FlightRecordIngestPayload<P extends MissionProvider = MissionProvider> = Omit<
    z.infer<typeof flightRecordIngestPayload>,
    "provider"
> & { provider: P }

export const pdfRasterIngestPayload = baseTagsSchema.extend({
    dataType: z.literal(IngestDataType.PdfRaster),
    projectId: z.string(),
    rasterId: z.string(),
    corners: z.string(),
})

export type PdfRasterIngestPayload = z.infer<typeof pdfRasterIngestPayload>

const pdfRasterCornerSchema = z.object({ latitude: latitudeSchema, longitude: longitudeSchema })

export const encodePdfRasterCorners = (corners: { latitude: number; longitude: number }[]) =>
    corners
        .map(({ latitude, longitude }) => `${latitude}${AXIS_SEPARATOR}${longitude}`)
        .join(CORNER_SEPARATOR)

export const pdfRasterCornersSchema = z
    .string()
    .transform((encoded) =>
        encoded.split(CORNER_SEPARATOR).map((corner) => {
            const [latitude, longitude] = corner.split(AXIS_SEPARATOR)
            return { latitude: Number(latitude), longitude: Number(longitude) }
        })
    )
    .pipe(
        z.tuple([
            pdfRasterCornerSchema,
            pdfRasterCornerSchema,
            pdfRasterCornerSchema,
            pdfRasterCornerSchema,
        ])
    )

export const csvLogbookIngestPayload = baseTagsSchema.extend({
    dataType: z.literal(IngestDataType.CsvLogbook),
    fileId: z.string(),
})

export type CsvLogbookIngestPayload = z.infer<typeof csvLogbookIngestPayload>

// @deprecated
// remove anytime after deploy of PR3750
export const rapidModeImageIngestPayload = baseTagsSchema.extend({
    dataType: z.literal(IngestDataType.RapidModeImage),
    projectId: z.string(),
    flightId: z.string(),
    totalCount: z.coerce.number().optional(),
    compression: z.enum(["gzip"]).optional(),
    channelType: z.enum(["IR", "RGB"]),
})

export type RapidModeImageIngestPayload = z.infer<typeof rapidModeImageIngestPayload>

export const commentImageIngestPayload = baseTagsSchema.extend({
    dataType: z.literal(IngestDataType.CommentImage),
    projectId: z.string(),
    entityId: z.string(),
})

export type CommentImageIngestPayload = z.infer<typeof commentImageIngestPayload>

export const ingestPayloadSchema = z.discriminatedUnion("dataType", [
    copyIngestPayload,
    infraredImageIngestPayload,
    orthoImageIngestPayload,
    panoramaIngestPayload,
    colorFinderImageIngestPayload,
    iconImageIngestPayload,
    profilePhotoIngestPayload,
    flightRecordIngestPayload,
    csvLogbookIngestPayload,
    commentImageIngestPayload,
    pdfRasterIngestPayload,
    // @deprecated
    // remove anytime after deploy of PR3750
    rapidModeImageIngestPayload,
])

export type IngestPayload = z.infer<typeof ingestPayloadSchema>
